import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_LOCALE, directionFor, isLocale } from './index';
import type { Direction, Locale } from '@/shared/types/api';

export interface LocaleController {
  locale: Locale;
  direction: Direction;
  isRtl: boolean;
  setLocale: (next: Locale) => void;
  toggle: () => void;
}

/** Reads and switches the active language, keeping <html dir> in step. */
export function useLocale(): LocaleController {
  const { i18n } = useTranslation();

  const locale: Locale = isLocale(i18n.resolvedLanguage)
    ? i18n.resolvedLanguage
    : isLocale(i18n.language)
      ? i18n.language
      : DEFAULT_LOCALE;

  const setLocale = useCallback(
    (next: Locale) => {
      void i18n.changeLanguage(next);
    },
    [i18n],
  );

  const toggle = useCallback(() => {
    setLocale(locale === 'ar' ? 'en' : 'ar');
  }, [locale, setLocale]);

  const direction = directionFor(locale);

  return { locale, direction, isRtl: direction === 'rtl', setLocale, toggle };
}
